/**
 * Identity & access (Identity mandate). Local-first multi-user model that lives
 * behind the Repository seam (ARCHITECTURE §3.2, §8): users, roles, and the
 * signed-in session persist in IndexedDB in this browser profile — no backend.
 * Swapping to a hosted auth backend later is a new Repository impl behind the
 * same interface, exactly like the ledger data.
 *
 * This module is pure app-infrastructure (like `AppMeta`): types, the RBAC
 * capability matrix, and password/session helpers. It imports nothing from
 * `core` or the UI, so the access rules can be unit-tested in isolation and are
 * enforced identically wherever the store runs a protected action.
 */

/** Access roles, most→least privileged. Tenant-scoped to the organization. */
export type Role = "owner" | "admin" | "member" | "viewer";

/** All roles, highest privilege first (invite/assign menus render in this order). */
export const ROLES: readonly Role[] = ["owner", "admin", "member", "viewer"];

/** A member of the organization (tenant). Passwords are never stored in clear. */
export interface User {
  readonly id: string;
  /** Organization (tenant) this user belongs to — see AppMeta.orgId. */
  readonly orgId: string;
  readonly name: string;
  /** Lowercased, unique within the organization; the sign-in identifier. */
  readonly email: string;
  readonly role: Role;
  /** Encoded verifier: `pbkdf2$iters$salt$hash` (see hashPassword). */
  readonly passwordHash: string;
  readonly createdAt: number;
}

/** An authenticated local session. Held in IndexedDB; expires after SESSION_TTL_MS. */
export interface Session {
  readonly userId: string;
  readonly orgId: string;
  readonly token: string;
  readonly createdAt: number;
  /** Epoch ms after which the session is no longer valid and the gate re-shows. */
  readonly expiresAt: number;
}

/** How long a local session stays valid before the sign-in gate returns. */
export const SESSION_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours

/** Protected capabilities. Every write in the store maps to one of these. */
export type Action =
  | "ledger.read"
  | "entry.post"
  | "entity.add"
  | "account.add"
  | "settings.edit"
  | "members.manage"
  | "data.reset";

const RANK: Record<Role, number> = { viewer: 0, member: 1, admin: 2, owner: 3 };

/**
 * The single source of truth for authorization (Identity mandate #2). Viewers
 * are read-only; only admin+ may add entities/accounts or post journal entries
 * or edit settings; only the owner may manage members or reset the tenant.
 * `member` is read-only in v1 with write scopes reserved for future granularity.
 */
export function can(role: Role | null | undefined, action: Action): boolean {
  if (!role) return false;
  switch (action) {
    case "ledger.read":
      return true; // every signed-in role, including viewer
    case "entry.post":
    case "entity.add":
    case "account.add":
    case "settings.edit":
      return RANK[role] >= RANK.admin;
    case "members.manage":
    case "data.reset":
      return role === "owner";
  }
}

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: "Full control — manage members, post entries, edit settings.",
  admin: "Post entries, add entities & accounts, edit settings.",
  member: "Read-only across the ledger (write scopes reserved).",
  viewer: "Read-only across the ledger.",
};

/** True when a session exists and has not expired at `now` (epoch ms). */
export function sessionValid(session: Session | null | undefined, now: number): boolean {
  return !!session && session.expiresAt > now;
}

/** Initials for the avatar, from a display name (max two letters). */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

// ---------------------------------------------------------------------------
// Password + token crypto. SubtleCrypto (PBKDF2-SHA256) in the browser; a
// clearly-labelled non-crypto fallback only where SubtleCrypto is absent
// (jsdom/SSR under test). Cloudflare Pages is served over HTTPS, so production
// always takes the PBKDF2 path.
// ---------------------------------------------------------------------------

const PBKDF2_ITERATIONS = 120_000;

function getSubtle(): SubtleCrypto | null {
  return typeof crypto !== "undefined" && crypto.subtle ? crypto.subtle : null;
}

function randomBytes(len: number): Uint8Array {
  const bytes = new Uint8Array(len);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < len; i++) bytes[i] = (i * 131 + 17) & 0xff; // fallback only
  }
  return bytes;
}

function toB64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i] as number);
  return btoa(s);
}

function fromB64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function deriveBits(
  subtle: SubtleCrypto,
  password: string,
  salt: Uint8Array,
  iterations: number,
): Promise<string> {
  const keyMaterial = await subtle.importKey(
    "raw",
    new TextEncoder().encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  );
  const bits = await subtle.deriveBits(
    { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" },
    keyMaterial,
    256,
  );
  return toB64(new Uint8Array(bits));
}

/** Non-cryptographic FNV-1a — used ONLY where SubtleCrypto is unavailable. */
function weakHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
}

/** Produce a salted, encoded password verifier suitable for persistence. */
export async function hashPassword(password: string): Promise<string> {
  const salt = randomBytes(16);
  const subtle = getSubtle();
  if (subtle) {
    const hash = await deriveBits(subtle, password, salt, PBKDF2_ITERATIONS);
    return `pbkdf2$${PBKDF2_ITERATIONS}$${toB64(salt)}$${hash}`;
  }
  return `weak$${toB64(salt)}$${weakHash(toB64(salt) + password)}`;
}

/** Verify a candidate password against a stored verifier. */
export async function verifyPassword(password: string, stored: string): Promise<boolean> {
  const parts = stored.split("$");
  // Demo/seed marker — only ever attached to dev seed users, never production.
  if (parts[0] === "demo") return stored === `demo$${password}`;
  if (parts[0] === "pbkdf2") {
    const subtle = getSubtle();
    if (!subtle) return false;
    const iterations = Number(parts[1]);
    const salt = fromB64(parts[2] ?? "");
    const hash = await deriveBits(subtle, password, salt, iterations);
    return hash === (parts[3] ?? "");
  }
  if (parts[0] === "weak") {
    return weakHash((parts[1] ?? "") + password) === (parts[2] ?? "");
  }
  return false;
}

/** A random, opaque session token. */
export function newToken(): string {
  return toB64(randomBytes(24));
}

/** A stable, unique user id. */
export function newUserId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `usr-${crypto.randomUUID()}`;
  }
  return `usr-${toB64(randomBytes(8)).replace(/[^a-z0-9]/gi, "")}`;
}

/** A stable, URL-safe organization (tenant) id derived from its name. */
export function orgIdFrom(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `org-${slug || "1"}`;
}
